import React, { useRef, useState, useCallback, useMemo } from 'react';
import { View, PanResponder, Dimensions, Platform } from 'react-native';
import Svg, { Path, Circle, G } from 'react-native-svg';
import { GestureHandlerRootView, PanGestureHandler, State } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedGestureHandler,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  runOnJS
} from 'react-native-reanimated';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialIcons } from '@expo/vector-icons';

interface Node {
  id: string;
  type: string;
  name: string;
  position: { x: number; y: number };
  config?: any;
}

interface Connection {
  source: { nodeId: string; outputIndex: number };
  target: { nodeId: string; inputIndex: number };
  type?: string;
}

interface WorkflowCanvasProps {
  nodes: Node[];
  connections: Connection[];
  onNodeMove?: (nodeId: string, position: { x: number; y: number }) => void;
  onNodeSelect?: (nodeId: string | null) => void;
  onConnectionCreate?: (sourceId: string, targetId: string) => void;
  onNodeAdd?: (type: string, position: { x: number; y: number }) => void;
  selectedNodeId?: string | null;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
}

const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  nodes = [],
  connections = [],
  onNodeMove,
  onNodeSelect,
  onConnectionCreate,
  onNodeAdd,
  selectedNodeId = null,
  zoom = 1,
  onZoomChange
}) => {
  const theme = themeSelector();
  const { width, height } = Dimensions.get('window');
  const [panState, setPanState] = useState({ x: 0, y: 0 });
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [connectionPreview, setConnectionPreview] = useState<{ x: number; y: number } | null>(null);

  const canvasRef = useRef<View>(null);

  // Node colors by type
  const getNodeColor = (type: string) => {
    const colors: Record<string, string> = {
      webhook: '#ff6d5a',
      schedule: '#00d9ff',
      manual: '#635bff',
      email: '#6bcf7f',
      calendar: '#6bcf7f',
      if: '#ffd93d',
      switch: '#ffd93d',
      loop: '#ffd93d',
      set: '#ff9500',
      transform: '#ff9500',
      filter: '#ff9500',
      errorTrigger: '#ff4444',
      errorHandler: '#ff4444'
    };
    return colors[type] || '#94a3b8';
  };

  // Calculate connection path
  const getConnectionPath = (source: Node, target: Node) => {
    const startX = source.position.x + 100;
    const startY = source.position.y + 50;
    const endX = target.position.x;
    const endY = target.position.y + 50;

    const midX = (startX + endX) / 2;
    const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;

    return path;
  };

  // Handle node press
  const handleNodePress = useCallback((nodeId: string) => {
    onNodeSelect?.(nodeId);
  }, [onNodeSelect]);

  // Handle node drag
  const handleNodeDrag = useCallback((nodeId: string, newPosition: { x: number; y: number }) => {
    onNodeMove?.(nodeId, newPosition);
  }, [onNodeMove]);

  // Render node
  const renderNode = (node: Node) => {
    const isSelected = selectedNodeId === node.id;
    const color = getNodeColor(node.type);

    return (
      <View
        key={node.id}
        style={{
          position: 'absolute',
          left: node.position.x + panState.x,
          top: node.position.y + panState.y,
          width: 200,
          minHeight: 100,
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderRadius: 12,
          borderWidth: isSelected ? 3 : 2,
          borderColor: isSelected ? color : (theme ? '#e2e8f0' : '#334155'),
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 4,
          padding: 12
        }}
        onTouchStart={() => handleNodePress(node.id)}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: color + '20',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 8
            }}
          >
            <MaterialIcons name="settings" size={18} color={color} />
          </View>
          <View style={{ flex: 1 }}>
            <View style={{ fontSize: 14, fontWeight: '600', color: theme ? '#1e293b' : '#f1f5f9' }}>
              {node.name}
            </View>
            <View style={{ fontSize: 11, color: theme ? '#64748b' : '#94a3b8', marginTop: 2 }}>
              {node.type}
            </View>
          </View>
        </View>
      </View>
    );
  };

  // Render connection
  const renderConnection = (connection: Connection) => {
    const sourceNode = nodes.find(n => n.id === connection.source.nodeId);
    const targetNode = nodes.find(n => n.id === connection.target.nodeId);

    if (!sourceNode || !targetNode) return null;

    const path = getConnectionPath(sourceNode, targetNode);

    return (
      <Path
        key={`${connection.source.nodeId}-${connection.target.nodeId}`}
        d={path}
        stroke={theme ? '#94a3b8' : '#475569'}
        strokeWidth={2}
        fill="none"
        strokeDasharray={connection.type === 'conditional' ? '5,5' : '0'}
      />
    );
  };

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <View
        ref={canvasRef}
        style={{
          flex: 1,
          backgroundColor: theme ? '#f8f9fa' : '#0f172a',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* SVG for connections */}
        <Svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: width * 2,
            height: height * 2,
            zIndex: 0
          }}
        >
          {connections.map(conn => renderConnection(conn))}
          {connectingFrom && connectionPreview && (
            <Path
              d={`M ${nodes.find(n => n.id === connectingFrom)?.position.x || 0} ${nodes.find(n => n.id === connectingFrom)?.position.y || 0} L ${connectionPreview.x} ${connectionPreview.y}`}
              stroke="#3b82f6"
              strokeWidth={2}
              fill="none"
              strokeDasharray="5,5"
            />
          )}
        </Svg>

        {/* Nodes */}
        <View style={{ position: 'relative', zIndex: 1 }}>
          {nodes.map(node => renderNode(node))}
        </View>

        {/* Zoom controls */}
        <View
          style={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            flexDirection: 'row',
            backgroundColor: theme ? '#ffffff' : '#1e293b',
            borderRadius: 8,
            padding: 8,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 4
          }}
        >
          <MaterialIcons
            name="zoom-out"
            size={24}
            color={theme ? '#1e293b' : '#f1f5f9'}
            onPress={() => onZoomChange?.(Math.max(0.5, zoom - 0.1))}
          />
          <View style={{ marginHorizontal: 12, justifyContent: 'center' }}>
            <View style={{ fontSize: 12, color: theme ? '#64748b' : '#94a3b8' }}>
              {Math.round(zoom * 100)}%
            </View>
          </View>
          <MaterialIcons
            name="zoom-in"
            size={24}
            color={theme ? '#1e293b' : '#f1f5f9'}
            onPress={() => onZoomChange?.(Math.min(2, zoom + 0.1))}
          />
        </View>
      </View>
    </GestureHandlerRootView>
  );
};

export default WorkflowCanvas;















